import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useSuspenseQuery, useQueryClient } from '@tanstack/react-query';
import { OrganizationInviteScreen } from '@/components/auth/OrganizationInviteScreen';
import { OrganizationJoinSuccess } from '@/components/auth/OrganizationJoinSuccess';
import { organizationsQueryOptions } from '@/lib/query-options';
import { determinePostAuthFlow, proceedWithNormalFlow } from '@/lib/invitation-flow';
import { acceptOrganizationInvitation, rejectOrganizationInvitation } from '@/lib/apis';
import { useUserProfile } from '@/contexts/user.context';
import { useToast } from '@/hooks/useToast';
import type { OrganizationDataWithMembership } from '@/lib/types';

export const Route = createFileRoute('/_authenticated/pending-invites')({
  component: PendingInvitesPage,
  errorComponent: ({ error }) => {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-red-600 mb-2">Route Error</h2>
          <p className="text-gray-700">Error: {error.message}</p>
        </div>
      </div>
    );
  },
});

function PendingInvitesPage() {
  try {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const { user } = useUserProfile();
  
  const [currentInviteIndex, setCurrentInviteIndex] = useState(0);
  const [isProcessingInvite, setIsProcessingInvite] = useState(false);
  const [showJoinSuccess, setShowJoinSuccess] = useState(false);
  const [acceptedOrganization, setAcceptedOrganization] = useState<OrganizationDataWithMembership | null>(null);
  
  // Get organizations data
  const { data: organizations } = useSuspenseQuery(organizationsQueryOptions());
  
  // Determine invite flow
  const inviteFlow = determinePostAuthFlow(organizations);
  
  
  // If no pending invites, redirect to normal flow
  useEffect(() => {
    if (!inviteFlow.shouldShowInvites || inviteFlow.pendingInvites.length === 0) {
      proceedWithNormalFlow(inviteFlow.activeOrganizations, user, navigate);
    }
  }, [inviteFlow.shouldShowInvites, inviteFlow.pendingInvites.length, inviteFlow.activeOrganizations, user, navigate]);
  
  // Get the current pending invite
  const pendingInvites = inviteFlow.pendingInvites;
  const currentInvite = pendingInvites[currentInviteIndex];
  
  
  const handleAcceptInvite = async (organizationId: number) => {
    setIsProcessingInvite(true);
    
    try {
      await acceptOrganizationInvitation(organizationId);
      
      // Invalidate and refetch organizations to get updated status
      await queryClient.invalidateQueries({ queryKey: ['organizations'] });
      
      // Set the accepted organization and show success screen
      setAcceptedOrganization(currentInvite);
      setShowJoinSuccess(true);
      
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to accept invitation',
        variant: 'destructive',
      });
      setIsProcessingInvite(false);
    }
  };
  
  const handleRejectInvite = async (organizationId: number) => {
    setIsProcessingInvite(true);
    
    try {
      await rejectOrganizationInvitation(organizationId);
      
      toast({
        title: 'Invitation Declined',
        description: 'You have declined the invitation.',
      });
      
      // Check if there are more invites
      if (currentInviteIndex < pendingInvites.length - 1) {
        setCurrentInviteIndex(currentInviteIndex + 1);
        setIsProcessingInvite(false);
      } else {
        // Invalidate and refetch organizations
        await queryClient.invalidateQueries({ queryKey: ['organizations'] });
        const updatedOrgs = await queryClient.fetchQuery(organizationsQueryOptions());
        
        // Get active organizations and proceed
        const activeOrgs = updatedOrgs.filter(org => 
          org.membership?.status === 'active'
        );
        
        proceedWithNormalFlow(activeOrgs, user, navigate);
      }
      
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to reject invitation',
        variant: 'destructive',
      });
      setIsProcessingInvite(false);
    }
  };

  const handleJoinSuccessComplete = () => {
    // Reset the success screen state
    setShowJoinSuccess(false);
    setAcceptedOrganization(null);
    setIsProcessingInvite(false);
    
    // The useEffect will handle redirecting if no more invites
    // Otherwise it will show the next invite
  };
  
  // Show success screen if invitation was accepted
  if (showJoinSuccess && acceptedOrganization) {
    return (
      <OrganizationJoinSuccess 
        organization={acceptedOrganization} 
        onContinue={handleJoinSuccessComplete}
      />
    );
  }
  
  // If no current invite, show a better message while redirecting
  if (!currentInvite) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Redirecting...</h2>
          <p className="text-gray-700">Please wait while we set up your account.</p>
        </div>
      </div>
    );
  }
  
  return (
    <OrganizationInviteScreen
      invite={currentInvite}
      onAccept={handleAcceptInvite}
      onReject={handleRejectInvite}
      isLoading={isProcessingInvite}
    />
  );
  } catch (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-red-600 mb-2">Error Loading Invitations</h2>
          <p className="text-gray-700">An error occurred while loading your invitations.</p>
        </div>
      </div>
    );
  }
}